import { useEffect, useMemo, useRef, useState } from "react";
import { api, BACKEND_URL, tokenStore } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Play, Square, Check, Pencil, FileBarChart, Plus, History, ShieldCheck,
  Download, Trophy, Activity, ListTree, MapPin, Lock, Unlock, FileText, ShieldAlert,
  Trash2,
} from "lucide-react";

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString("nb-NO", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  }) : "—";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const fromLocalInput = (v) => v ? new Date(v).toISOString() : null;

function LiveClock({ startedAt }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);
  const ms = Date.now() - new Date(startedAt).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return (
    <span className="font-mono tabular-nums text-3xl" data-testid="time-live-clock" data-tick={tick}>
      {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
    </span>
  );
}

function EditRow({ entry, onSave, onCancel }) {
  const [start, setStart] = useState(toLocalInput(entry.started_at));
  const [end, setEnd] = useState(toLocalInput(entry.ended_at));
  const [note, setNote] = useState(entry.note || "");
  return (
    <div className="grid sm:grid-cols-[1fr_1fr_2fr_auto] gap-2 items-end">
      <div>
        <Label className="text-xs">Start</Label>
        <Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)}
               data-testid={`time-edit-start-${entry.id}`} />
      </div>
      <div>
        <Label className="text-xs">Stopp</Label>
        <Input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)}
               data-testid={`time-edit-end-${entry.id}`} />
      </div>
      <div>
        <Label className="text-xs">Notat</Label>
        <Input value={note} onChange={(e) => setNote(e.target.value)}
               data-testid={`time-edit-note-${entry.id}`} />
      </div>
      <div className="flex gap-1">
        <Button size="sm" onClick={() => onSave({
          started_at: fromLocalInput(start),
          ended_at: fromLocalInput(end),
          note,
        })} data-testid={`time-edit-save-${entry.id}`}
                className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white">Lagre</Button>
        <Button size="sm" variant="outline" onClick={onCancel}>Avbryt</Button>
      </div>
    </div>
  );
}

function AuditHistory({ entry }) {
  if (!entry.audit_log || entry.audit_log.length === 0) return null;
  return (
    <details className="mt-2" data-testid={`time-audit-${entry.id}`}>
      <summary className="text-xs text-[#A57F39] cursor-pointer inline-flex items-center gap-1.5 hover:underline">
        <History className="w-3 h-3" /> Endringshistorikk ({entry.audit_log.length})
      </summary>
      <div className="mt-2 space-y-1.5 text-xs text-[#2B3F4D] border-l-2 border-[#BD984D]/30 pl-3">
        {entry.audit_log.map((a, i) => (
          <div key={i}>
            <span className="font-semibold text-[#0C1824]">
              {a.action === "created" && "Opprettet"}
              {a.action === "created_by_admin" && "Opprettet av admin"}
              {a.action === "edited" && "Endret"}
              {a.action === "edited_by_admin" && "Endret av admin"}
              {a.action === "confirmed" && "Bekreftet"}
              {a.action === "confirmed_by_admin" && "Bekreftet av admin"}
            </span>
            {" – "}{a.user_name}{" · "}{fmt(a.timestamp)}
          </div>
        ))}
      </div>
    </details>
  );
}

function EntryCard({ entry, onConfirm, onEdit, editingId, setEditing, onSaveEdit, onDelete, viewerIsAdmin }) {
  const adminFlag = entry.created_by_role === "admin" && entry.created_by_id !== entry.user_id;
  const monthLocked = entry.month_locked === true;
  return (
    <Card className={`p-3 sm:p-4 ${entry.confirmed ? "border-[#1DA3B3]/40" : ""} ${
      monthLocked ? "bg-slate-50/50 border-[#BD984D]/40" : ""
    }`} data-testid={`time-row-${entry.id}`}>
      {editingId === entry.id ? (
        <EditRow entry={entry} onCancel={() => setEditing(null)}
                 onSave={(patch) => onSaveEdit(entry.id, patch)} />
      ) : (
        <div className="flex items-start justify-between gap-2 sm:gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
              <span className="text-base sm:text-lg font-bold text-[#0C1824]">{entry.hours ?? "—"} t</span>
              {monthLocked && (
                <Badge className="bg-[#BD984D]/20 text-[#A57F39] border-0">
                  <Lock className="w-3 h-3 mr-1" /> Måned låst
                </Badge>
              )}
              {entry.confirmed && (
                <Badge className="bg-[#1DA3B3]/15 text-[#1DA3B3] border-0">Bekreftet</Badge>
              )}
              {!entry.ended_at && (
                <Badge className="bg-emerald-100 text-emerald-700 border-0">Pågår</Badge>
              )}
              {adminFlag && (
                <Badge className="bg-[#BD984D]/15 text-[#A57F39] border-0"
                       data-testid={`time-admin-flag-${entry.id}`}>
                  <ShieldCheck className="w-3 h-3 mr-1" />
                  Lagt til av admin: {entry.created_by_name}
                </Badge>
              )}
            </div>
            <div className="text-sm text-[#2B3F4D] mt-1">
              {fmt(entry.started_at)} → {fmt(entry.ended_at)}
            </div>
            {entry.note && <div className="text-sm text-[#0C1824] mt-1 italic">«{entry.note}»</div>}
            <AuditHistory entry={entry} />
          </div>
          <div className="flex gap-1">
            {!monthLocked && (viewerIsAdmin || (!entry.confirmed && entry.ended_at)) && (
              <Button size="sm" variant="outline" onClick={() => onEdit(entry.id)}
                      data-testid={`time-edit-${entry.id}`}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            )}
            {!monthLocked && !entry.confirmed && entry.ended_at && (
              <Button size="sm" onClick={() => onConfirm(entry.id)}
                      data-testid={`time-confirm-${entry.id}`}
                      className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white">
                <Check className="w-3.5 h-3.5 mr-1" /> Bekreft
              </Button>
            )}
            {!monthLocked && !entry.confirmed && entry.ended_at && onDelete && (
              <Button size="sm" variant="outline" onClick={() => onDelete(entry.id)}
                      data-testid={`time-delete-${entry.id}`}
                      className="border-red-300 text-red-600 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function AdminAddEntryDialog({ targetUserId, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ start: "", end: "", note: "" });

  const submit = async () => {
    if (!form.start || !form.end) return toast.error("Velg start og slutt");
    try {
      await api.post("/time", {
        started_at: fromLocalInput(form.start),
        ended_at: fromLocalInput(form.end),
        note: form.note,
        target_user_id: targetUserId,
      });
      toast.success("Timer ført – brukeren ser dem nå");
      setOpen(false);
      setForm({ start: "", end: "", note: "" });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="admin-time-add-open"
                className="bg-[#BD984D] hover:bg-[#A57F39] text-white w-full">
          <Plus className="w-4 h-4 mr-1.5" /> Før timer for ansatt
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Før timer manuelt</DialogTitle>
          <DialogDescription>
            Lag en ny timeoppføring på den valgte ansattes konto. Endringen logges som
            «Lagt til av admin» og er synlig for den ansatte og andre admins.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Start</Label>
            <Input type="datetime-local" value={form.start}
                   onChange={(e) => setForm({ ...form, start: e.target.value })}
                   data-testid="admin-time-add-start" />
          </div>
          <div>
            <Label>Stopp</Label>
            <Input type="datetime-local" value={form.end}
                   onChange={(e) => setForm({ ...form, end: e.target.value })}
                   data-testid="admin-time-add-end" />
          </div>
          <div>
            <Label>Notat (valgfritt)</Label>
            <Input value={form.note}
                   onChange={(e) => setForm({ ...form, note: e.target.value })}
                   placeholder="F.eks. Glemt å føre – etterregistrert"
                   data-testid="admin-time-add-note" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} data-testid="admin-time-add-save"
                  className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white">
            Lagre
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MyTimesheet({ active, entries, note, setNote, onStart, onStop,
                       onConfirm, onSaveEdit, onDelete, editing, setEditing,
                       range, setRange }) {
  const totalHours = useMemo(
    () => entries.reduce((acc, e) => acc + (e.hours || 0), 0).toFixed(2),
    [entries],
  );
  const confirmedHours = useMemo(
    () => entries.filter((e) => e.confirmed).reduce((acc, e) => acc + (e.hours || 0), 0).toFixed(2),
    [entries],
  );

  const setPreset = (preset) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    if (preset === "all") return setRange({ from: "", to: "" });
    if (preset === "this_month") {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return setRange({ from: iso(first), to: iso(now) });
    }
    if (preset === "last_month") {
      const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const last = new Date(now.getFullYear(), now.getMonth(), 0);
      return setRange({ from: iso(first), to: iso(last) });
    }
    if (preset === "last_7") {
      const from = new Date(now.getTime() - 6 * 86400000);
      return setRange({ from: iso(from), to: iso(now) });
    }
    if (preset === "last_30") {
      const from = new Date(now.getTime() - 29 * 86400000);
      return setRange({ from: iso(from), to: iso(now) });
    }
    if (preset === "ytd") {
      const first = new Date(now.getFullYear(), 0, 1);
      return setRange({ from: iso(first), to: iso(now) });
    }
  };
  const rangeIsActive = !!(range?.from || range?.to);

  return (
    <div className="space-y-6">
      <Card className="p-4 sm:p-6 bg-gradient-to-br from-[#0C1824] to-[#11283A] text-white"
            data-testid="time-card">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-1.5 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">
              {active ? "Pågående arbeidsøkt" : "Klar til å starte"}
            </div>
            {active ? (
              <>
                <LiveClock startedAt={active.started_at} />
                <div className="text-xs text-white/70 break-words">
                  Startet {fmt(active.started_at)}
                  {active.note && ` · ${active.note}`}
                </div>
              </>
            ) : (
              <p className="text-sm text-white/80 max-w-md">
                Skriv valgfritt notat (f.eks. «Renhold Skien») og trykk Start.
              </p>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-end w-full sm:w-auto">
            {!active && (
              <Input value={note} onChange={(e) => setNote(e.target.value)}
                     placeholder="Notat (valgfritt)" data-testid="time-start-note"
                     className="bg-white text-[#0C1824] w-full sm:w-56" />
            )}
            {active ? (
              <Button onClick={onStop} data-testid="time-stop"
                      className="bg-red-500 hover:bg-red-600 text-white h-12 px-6 w-full sm:w-auto">
                <Square className="w-4 h-4 mr-1.5" /> Stopp
              </Button>
            ) : (
              <Button onClick={onStart} data-testid="time-start"
                      className="bg-emerald-500 hover:bg-emerald-600 text-white h-12 px-6 w-full sm:w-auto">
                <Play className="w-4 h-4 mr-1.5" /> Start
              </Button>
            )}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        <Card className="p-3 sm:p-4"><div className="text-[10px] sm:text-xs text-[#A57F39]">Totalt registrert</div>
          <div className="text-lg sm:text-2xl font-bold text-[#0C1824] mt-1">{totalHours} t</div></Card>
        <Card className="p-3 sm:p-4"><div className="text-[10px] sm:text-xs text-[#A57F39]">Bekreftet</div>
          <div className="text-lg sm:text-2xl font-bold text-[#1DA3B3] mt-1">{confirmedHours} t</div></Card>
        <Card className="p-3 sm:p-4"><div className="text-[10px] sm:text-xs text-[#A57F39]">Oppføringer</div>
          <div className="text-lg sm:text-2xl font-bold text-[#0C1824] mt-1">{entries.length}</div></Card>
      </div>

      <div className="space-y-3" data-testid="time-entries">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="text-lg font-semibold text-[#0C1824]">Dine oppføringer</h2>
          {rangeIsActive && (
            <button
              onClick={() => setPreset("all")}
              className="text-xs text-[#A57F39] hover:text-[#BD984D] underline"
              data-testid="my-time-range-clear"
            >
              Fjern periodefilter
            </button>
          )}
        </div>
        <Card className="p-3">
          <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_auto] gap-2 sm:gap-3 items-end">
            <div>
              <Label className="text-xs">Fra dato</Label>
              <Input
                type="date"
                value={range?.from || ""}
                onChange={(e) => setRange({ ...(range || {}), from: e.target.value })}
                data-testid="my-time-range-from"
              />
            </div>
            <div>
              <Label className="text-xs">Til dato</Label>
              <Input
                type="date"
                value={range?.to || ""}
                onChange={(e) => setRange({ ...(range || {}), to: e.target.value })}
                data-testid="my-time-range-to"
              />
            </div>
            <div className="text-[11px] text-[#2B3F4D] col-span-2 sm:col-auto">
              {rangeIsActive ? (
                <>Viser {range.from || "…"} – {range.to || "i dag"}</>
              ) : (
                <>Viser alle registrerte timer</>
              )}
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {[
              ["this_month", "Denne måneden"],
              ["last_month", "Forrige måned"],
              ["last_7", "Siste 7 dager"],
              ["last_30", "Siste 30 dager"],
              ["ytd", "Hittil i år"],
              ["all", "Alle"],
            ].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setPreset(k)}
                className="text-[11px] px-2.5 py-1 rounded-full border border-[#BD984D]/30 text-[#A57F39] hover:bg-[#BD984D]/10"
                data-testid={`my-time-preset-${k}`}
              >
                {label}
              </button>
            ))}
          </div>
        </Card>
        {entries.length === 0 && (
          <Card className="p-6 text-sm text-[#2B3F4D]">
            {rangeIsActive
              ? "Ingen registrerte timer i valgt periode."
              : "Ingen registrerte timer."}
          </Card>
        )}
        {entries.map((e) => (
          <EntryCard
            key={e.id} entry={e} viewerIsAdmin={false}
            editingId={editing} setEditing={setEditing}
            onConfirm={onConfirm}
            onEdit={(id) => setEditing(id)} onSaveEdit={onSaveEdit}
            onDelete={onDelete}
          />
        ))}
      </div>
    </div>
  );
}

function AdminManageStaff({ refreshOwn }) {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState("");
  const [entries, setEntries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });

  useEffect(() => {
    api.get("/users").then((r) => {
      setUsers(r.data.filter((u) => ["service", "vask", "admin"].includes(u.role)));
    });
  }, []);

  const reload = async (uid = selected) => {
    if (!uid) return setEntries([]);
    const params = { user_id: uid };
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    const r = await api.get("/time", { params });
    setEntries(r.data);
  };
  useEffect(() => { reload(); }, [selected, range]);

  const onConfirm = async (id) => {
    await api.post(`/time/${id}/confirm`);
    toast.success("Bekreftet");
    reload();
  };
  const onSaveEdit = async (id, patch) => {
    try {
      await api.patch(`/time/${id}`, patch);
      toast.success("Oppdatert – endring logget");
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Slette denne timeoppføringen? Dette kan ikke angres.")) return;
    try {
      await api.delete(`/time/${id}`);
      toast.success("Slettet");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette");
    }
  };

  const totals = useMemo(() => {
    const tot = entries.reduce((a, e) => a + (e.hours || 0), 0);
    const con = entries.filter((e) => e.confirmed).reduce((a, e) => a + (e.hours || 0), 0);
    return { total: tot.toFixed(2), confirmed: con.toFixed(2),
             unconfirmed: (tot - con).toFixed(2) };
  }, [entries]);

  const selectedUser = users.find((u) => u.id === selected);

  return (
    <div className="space-y-5">
      <Card className="p-4 sm:p-5 bg-gradient-to-br from-[#11283A] to-[#0C1824] text-white">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">
          Adminadministrasjon
        </div>
        <h2 className="text-lg sm:text-xl font-semibold mt-1">Endre og legg til timer for ansatte</h2>
        <p className="text-xs sm:text-sm text-white/80 mt-1.5 max-w-2xl">
          Velg en ansatt for å se, korrigere og legge til timer. Alle endringer logges
          med navn og tidspunkt – synlig for andre admins og den ansatte selv.
        </p>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 sm:gap-3 items-end">
        <div className="col-span-2 sm:col-span-1">
          <Label className="text-xs">Velg ansatt</Label>
          <Select value={selected || "__none__"}
                  onValueChange={(v) => setSelected(v === "__none__" ? "" : v)}>
            <SelectTrigger data-testid="admin-time-user-select"><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Velg ansatt</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>
                  {u.name} ({u.role})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Fra dato</Label>
          <Input type="date" value={range.from}
                 onChange={(e) => setRange({ ...range, from: e.target.value })}
                 data-testid="admin-time-range-from" />
        </div>
        <div>
          <Label className="text-xs">Til dato</Label>
          <Input type="date" value={range.to}
                 onChange={(e) => setRange({ ...range, to: e.target.value })}
                 data-testid="admin-time-range-to" />
        </div>
        {selected && (
          <div className="col-span-2 sm:col-span-1">
            <AdminAddEntryDialog targetUserId={selected} onSaved={() => { reload(); refreshOwn(); }} />
          </div>
        )}
      </div>

      {!selected && (
        <Card className="p-6 text-sm text-[#2B3F4D]">
          Velg en ansatt for å se og redigere timene deres.
        </Card>
      )}

      {selected && (
        <>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <Card className="p-3 sm:p-4">
              <div className="text-[10px] sm:text-xs text-[#A57F39] truncate">{selectedUser?.name} · Totalt</div>
              <div className="text-lg sm:text-2xl font-bold text-[#0C1824] mt-1" data-testid="admin-time-total">{totals.total} t</div>
            </Card>
            <Card className="p-3 sm:p-4">
              <div className="text-[10px] sm:text-xs text-[#A57F39]">Bekreftet</div>
              <div className="text-lg sm:text-2xl font-bold text-[#1DA3B3] mt-1">{totals.confirmed} t</div>
            </Card>
            <Card className="p-3 sm:p-4">
              <div className="text-[10px] sm:text-xs text-[#A57F39]">Ubekreftet</div>
              <div className="text-lg sm:text-2xl font-bold text-[#A57F39] mt-1">{totals.unconfirmed} t</div>
            </Card>
          </div>

          <div className="space-y-3" data-testid="admin-time-entries">
            {entries.length === 0 && (
              <Card className="p-6 text-sm text-[#2B3F4D]">
                Ingen registrerte timer for {selectedUser?.name} i valgt periode.
              </Card>
            )}
            {entries.map((e) => (
              <EntryCard
                key={e.id} entry={e} viewerIsAdmin={true}
                editingId={editing} setEditing={setEditing}
                onConfirm={onConfirm}
                onEdit={(id) => setEditing(id)} onSaveEdit={onSaveEdit}
                onDelete={onDelete}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function HourBar({ value, max, color = "#1DA3B3" }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full transition-all"
           style={{ width: `${pct}%`, backgroundColor: color }} />
    </div>
  );
}

const fmtShortDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("nb-NO", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
  });
};

const elapsedSince = (iso) => {
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  return `${h} t ${m} min`;
};

function AdminActivity() {
  const [active, setActive] = useState([]);
  const [feed, setFeed] = useState([]);
  const [users, setUsers] = useState([]);
  const [boats, setBoats] = useState([]);
  const [filter, setFilter] = useState({ from: "", to: "", user_id: "", boat_id: "" });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    api.get("/users").then((r) => setUsers(r.data.filter((u) => ["admin","service","vask"].includes(u.role))));
    api.get("/boats").then((r) => setBoats(r.data));
  }, []);

  const loadActive = async () => {
    const r = await api.get("/time/active-now");
    setActive(r.data);
  };
  const loadFeed = async () => {
    const params = {};
    if (filter.from) params.date_from = filter.from;
    if (filter.to) params.date_to = filter.to;
    if (filter.user_id) params.user_id = filter.user_id;
    if (filter.boat_id) params.boat_id = filter.boat_id;
    const r = await api.get("/time/activity", { params });
    setFeed(r.data);
  };

  useEffect(() => { loadActive(); loadFeed(); }, []);
  useEffect(() => { loadFeed(); }, [filter]); // eslint-disable-line

  // Re-tick the live clock on active sessions every 30s
  useEffect(() => {
    const t = setInterval(() => { setTick((x) => x + 1); loadActive(); }, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-5">
      <Card className="p-4 sm:p-5 bg-gradient-to-br from-[#11283A] to-[#0C1824] text-white"
            data-testid="admin-activity-live">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">
          <Activity className="w-3.5 h-3.5" /> Pågående nå
          <span className="text-white/60 text-[10px] ml-1">· {active.length} klokket inn</span>
        </div>
        {active.length === 0 ? (
          <div className="text-sm text-white/70 mt-3">Ingen er klokket inn akkurat nå.</div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3 mt-4">
            {active.map((a) => (
              <div key={a.id} data-testid={`admin-active-${a.user_id}`}
                   className="rounded-xl bg-white/5 border border-white/10 p-3 sm:p-3.5">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-sm sm:text-base truncate min-w-0">{a.user_name}</div>
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-0 shrink-0">
                    {a.user_role}
                  </Badge>
                </div>
                <div className="text-[11px] text-white/60 mt-1">
                  Startet {fmtShortDate(a.started_at)} · {elapsedSince(a.started_at)}
                </div>
                {a.boat_name && (
                  <div className="text-xs text-[#ECC974] mt-1.5">{a.boat_name}</div>
                )}
                {a.note && (
                  <div className="text-xs text-white/85 mt-1 italic line-clamp-2 break-words">«{a.note}»</div>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="text-[10px] text-white/40 mt-3" data-tick={tick}>
          Oppdateres automatisk hvert 30. sekund
        </div>
      </Card>

      <div className="grid grid-cols-2 sm:grid-cols-[1.5fr_1fr_1fr_1fr] gap-2 sm:gap-3 items-end">
        <div className="col-span-2 sm:col-span-1">
          <Label className="text-xs">Ansatt</Label>
          <Select value={filter.user_id || "__all__"}
                  onValueChange={(v) => setFilter({ ...filter, user_id: v === "__all__" ? "" : v })}>
            <SelectTrigger data-testid="activity-filter-user"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Alle ansatte</SelectItem>
              {users.map((u) => (
                <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Båt</Label>
          <Select value={filter.boat_id || "__all__"}
                  onValueChange={(v) => setFilter({ ...filter, boat_id: v === "__all__" ? "" : v })}>
            <SelectTrigger data-testid="activity-filter-boat"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Alle båter</SelectItem>
              {boats.map((b) => (
                <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Fra</Label>
          <Input type="date" value={filter.from}
                 onChange={(e) => setFilter({ ...filter, from: e.target.value })}
                 data-testid="activity-filter-from" />
        </div>
        <div>
          <Label className="text-xs">Til</Label>
          <Input type="date" value={filter.to}
                 onChange={(e) => setFilter({ ...filter, to: e.target.value })}
                 data-testid="activity-filter-to" />
        </div>
      </div>

      <div className="space-y-2" data-testid="activity-feed">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#A57F39]">
          <ListTree className="w-3.5 h-3.5" /> Hendelseslogg · {feed.length} oppføring{feed.length === 1 ? "" : "er"}
        </div>
        {feed.length === 0 && (
          <Card className="p-6 text-sm text-[#2B3F4D]">Ingen aktivitet i valgt periode.</Card>
        )}
        {feed.map((e) => (
          <Card key={e.id} data-testid={`activity-row-${e.id}`}
                className="p-3 sm:p-4 flex items-start gap-2 sm:gap-3 hover:border-[#BD984D]/40">
            <div className="text-[10px] uppercase tracking-[0.15em] text-[#A57F39] font-mono whitespace-nowrap mt-1 shrink-0">
              {e.started_at?.slice(5, 10)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                <span className="font-semibold text-[#0C1824] text-sm sm:text-base">{e.user_name}</span>
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#A57F39]">{e.user_role}</span>
                {e.boat_name && (
                  <Badge className="bg-[#BD984D]/15 text-[#A57F39] border-0 text-[10px]">{e.boat_name}</Badge>
                )}
                {e.confirmed && (
                  <Badge className="bg-[#1DA3B3]/15 text-[#1DA3B3] border-0 text-[10px]">Bekreftet</Badge>
                )}
              </div>
              <div className="text-[11px] sm:text-xs text-[#2B3F4D] mt-1 break-words">
                {fmtShortDate(e.started_at)} → {e.ended_at ? fmtShortDate(e.ended_at) : "pågår"}
                {" · "}<strong className="text-[#0C1824]">{e.hours ?? "—"} t</strong>
              </div>
              {e.note && (
                <div className="text-xs sm:text-sm text-[#0C1824] mt-1.5 italic break-words">«{e.note}»</div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function AdminReport() {
  const [report, setReport] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const [granularity, setGranularity] = useState("month"); // day | week | month | total

  const load = async () => {
    const params = { granularity };
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    const r = await api.get("/time/report", { params });
    setReport(r.data);
  };
  useEffect(() => { load(); }, [granularity]); // eslint-disable-line

  const downloadCsv = () => {
    const token = tokenStore.get();
    const params = new URLSearchParams();
    if (range.from) params.set("date_from", range.from);
    if (range.to) params.set("date_to", range.to);
    // Bearer header isn't possible on a direct link, so we attach a one-time
    // fetch + blob download instead. This keeps the token out of the URL.
    fetch(`${BACKEND_URL}/api/time/report.csv?${params.toString()}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((b) => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url;
        a.download = `timeliste_${range.from || "all"}_til_${range.to || "all"}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
        URL.revokeObjectURL(url);
        toast.success("CSV lastet ned");
      })
      .catch(() => toast.error("Kunne ikke laste ned CSV"));
  };

  const maxBucketHours = (report?.buckets || []).reduce((m, b) => Math.max(m, b.total_hours), 0);
  const maxUserHours = (report?.rows || []).reduce((m, r) => Math.max(m, r.total_hours), 0);
  const maxBoatHours = (report?.boats || []).reduce((m, b) => Math.max(m, b.total_hours), 0);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-end gap-2 sm:gap-3">
        <div>
          <Label className="text-xs">Granularitet</Label>
          <Select value={granularity} onValueChange={setGranularity}>
            <SelectTrigger className="w-full sm:w-36" data-testid="time-report-granularity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="day">Daglig</SelectItem>
              <SelectItem value="week">Ukentlig</SelectItem>
              <SelectItem value="month">Månedlig</SelectItem>
              <SelectItem value="total">Bare totaler</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Fra</Label>
          <Input type="date" value={range.from}
                 onChange={(e) => setRange({ ...range, from: e.target.value })}
                 data-testid="time-report-from" />
        </div>
        <div className="col-span-2 sm:col-auto">
          <Label className="text-xs">Til</Label>
          <Input type="date" value={range.to}
                 onChange={(e) => setRange({ ...range, to: e.target.value })}
                 data-testid="time-report-to" />
        </div>
        <Button onClick={load} data-testid="time-report-refresh"
                className="bg-[#BD984D] hover:bg-[#A57F39] text-white flex-1 sm:flex-none">
          <FileBarChart className="w-4 h-4 mr-1.5" /> Oppdater
        </Button>
        <Button onClick={downloadCsv} variant="outline"
                data-testid="time-report-csv"
                className="border-[#1DA3B3] text-[#1DA3B3] hover:bg-[#1DA3B3]/10 flex-1 sm:flex-none">
          <Download className="w-4 h-4 mr-1.5" /> CSV
        </Button>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            <Card className="p-3 sm:p-5">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[#A57F39]">Totalt</div>
              <div className="text-xl sm:text-3xl font-bold text-[#0C1824] mt-1"
                   data-testid="time-report-total">{report.total_hours} t</div>
              <div className="text-[10px] sm:text-xs text-[#2B3F4D] mt-1 truncate">{report.rows.length} ansatte aktive</div>
            </Card>
            <Card className="p-3 sm:p-5">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[#A57F39]">Snitt/ansatt/dag</div>
              <div className="text-xl sm:text-3xl font-bold text-[#1DA3B3] mt-1">
                {report.rows.length === 0 ? "0" : (
                  (report.rows.reduce((a, r) => a + r.avg_hours_per_day, 0) / report.rows.length).toFixed(2)
                )} t
              </div>
              <div className="text-[10px] sm:text-xs text-[#2B3F4D] mt-1 truncate">Effektivitet</div>
            </Card>
            <Card className="p-3 sm:p-5">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-[#A57F39]">Båter</div>
              <div className="text-xl sm:text-3xl font-bold text-[#0C1824] mt-1">{report.boats.length}</div>
              <div className="text-[10px] sm:text-xs text-[#2B3F4D] mt-1 truncate">med registrert tid</div>
            </Card>
          </div>

          {/* Top 3 leaderboard */}
          {report.top && report.top.length > 0 && (
            <Card className="p-5" data-testid="time-report-top">
              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-[#BD984D]" />
                <h3 className="text-lg font-semibold text-[#0C1824]">Topp 3 – flest registrerte timer</h3>
              </div>
              <div className="space-y-3">
                {report.top.map((r, idx) => {
                  const rankBg = ["bg-[#BD984D]", "bg-slate-400", "bg-amber-700"][idx] || "bg-slate-300";
                  return (
                    <div key={r.user_id} className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full ${rankBg} text-white flex items-center justify-center font-bold text-sm shrink-0`}>
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                          <div className="font-semibold text-[#0C1824] truncate text-sm sm:text-base">
                            {r.user_name} <span className="text-[10px] uppercase text-[#A57F39] ml-1">{r.user_role}</span>
                          </div>
                          <div className="text-xs sm:text-sm font-mono text-[#0C1824]">
                            <strong>{r.total_hours} t</strong>
                            <span className="text-[10px] sm:text-xs text-[#1DA3B3] ml-2">({r.confirmed_hours} bekr.)</span>
                          </div>
                        </div>
                        <HourBar value={r.total_hours} max={maxUserHours} color="#BD984D" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* Bucket chart */}
          {granularity !== "total" && report.buckets.length > 0 && (
            <Card className="p-5" data-testid="time-report-buckets">
              <h3 className="text-lg font-semibold text-[#0C1824] mb-4">
                Timer per {granularity === "day" ? "dag" : granularity === "week" ? "uke" : "måned"}
              </h3>
              <div className="space-y-2.5">
                {report.buckets.map((b) => (
                  <div key={b.bucket} className="flex items-center gap-2 sm:gap-3">
                    <div className="w-16 sm:w-24 text-[11px] sm:text-xs font-mono text-[#2B3F4D] shrink-0">{b.bucket}</div>
                    <div className="flex-1 min-w-0">
                      <HourBar value={b.total_hours} max={maxBucketHours} color="#1DA3B3" />
                    </div>
                    <div className="w-16 sm:w-24 text-right text-[11px] sm:text-sm font-mono text-[#0C1824] shrink-0">
                      <strong>{b.total_hours} t</strong>
                      <span className="text-[10px] text-[#2B3F4D] ml-1">/ {b.entries}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Per-boat breakdown */}
          {report.boats.length > 0 && (
            <Card className="p-5" data-testid="time-report-boats">
              <h3 className="text-lg font-semibold text-[#0C1824] mb-4">Timer per båt</h3>
              <div className="space-y-2.5">
                {report.boats.map((b) => (
                  <div key={b.boat_id} className="flex items-center gap-2 sm:gap-3">
                    <div className="w-24 sm:w-40 text-xs sm:text-sm font-semibold text-[#0C1824] truncate shrink-0">{b.boat_name}</div>
                    <div className="flex-1 min-w-0">
                      <HourBar value={b.total_hours} max={maxBoatHours} color="#0C1824" />
                    </div>
                    <div className="w-14 sm:w-24 text-right text-xs sm:text-sm font-mono text-[#0C1824] shrink-0">
                      <strong>{b.total_hours} t</strong>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Full per-user table */}
          <Card className="p-5" data-testid="time-report">
            <h3 className="text-lg font-semibold text-[#0C1824] mb-3">Alle ansatte</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[#A57F39] border-b border-[#BD984D]/25">
                    <th className="py-2">Bruker</th>
                    <th>Rolle</th>
                    <th>Bekreftet</th>
                    <th>Ubekreftet</th>
                    <th>Total</th>
                    <th>Dager aktiv</th>
                    <th>Snitt/dag</th>
                    <th>Oppføringer</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map((r) => (
                    <tr key={r.user_id} className="border-b border-slate-100">
                      <td className="py-2 font-semibold text-[#0C1824]">{r.user_name}</td>
                      <td className="text-[#2B3F4D]">{r.user_role}</td>
                      <td className="text-[#1DA3B3]">{r.confirmed_hours} t</td>
                      <td className="text-[#A57F39]">{r.unconfirmed_hours} t</td>
                      <td className="font-bold text-[#0C1824]">{r.total_hours} t</td>
                      <td className="text-[#2B3F4D]">{r.days_active}</td>
                      <td className="text-[#0C1824]">{r.avg_hours_per_day} t</td>
                      <td className="text-[#2B3F4D]">{r.entry_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

function AdminMonths({ user, onChanged }) {
  const [months, setMonths] = useState([]);
  const [busy, setBusy] = useState({});
  const [approveOpen, setApproveOpen] = useState(null); // month being approved
  const [approveNote, setApproveNote] = useState("");

  const load = async () => {
    const r = await api.get("/time/months");
    setMonths(r.data);
  };
  useEffect(() => { load(); }, []);

  const fmtMonth = (m) => {
    const [y, mm] = m.split("-");
    const names = ["januar","februar","mars","april","mai","juni","juli",
                   "august","september","oktober","november","desember"];
    return `${names[parseInt(mm,10)-1]} ${y}`;
  };

  const downloadPdf = async (month) => {
    const token = tokenStore.get();
    setBusy((b) => ({ ...b, [month]: "pdf" }));
    try {
      const r = await fetch(`${BACKEND_URL}/api/time/months/${month}/export.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(await r.text());
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `timeliste_${month}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF lastet ned");
    } catch (e) {
      toast.error("Kunne ikke laste ned PDF");
    } finally {
      setBusy((b) => ({ ...b, [month]: null }));
    }
  };

  const approve = async (month) => {
    setBusy((b) => ({ ...b, [month]: "approve" }));
    try {
      await api.post(`/time/months/${month}/approve`, { note: approveNote.trim() });
      toast.success(`${fmtMonth(month)} er godkjent og låst`);
      setApproveOpen(null);
      setApproveNote("");
      load();
      onChanged && onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke godkjenne");
    } finally {
      setBusy((b) => ({ ...b, [month]: null }));
    }
  };

  const unlock = async (month) => {
    if (!window.confirm(`Låse opp ${fmtMonth(month)}? Brukere vil kunne endre timer igjen.`)) return;
    setBusy((b) => ({ ...b, [month]: "unlock" }));
    try {
      await api.post(`/time/months/${month}/unlock`);
      toast.success("Måneden er låst opp");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke låse opp");
    } finally {
      setBusy((b) => ({ ...b, [month]: null }));
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-4 sm:p-5 bg-gradient-to-br from-[#11283A] to-[#0C1824] text-white">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-[#ECC974]">
          <Lock className="w-3.5 h-3.5" /> Måneds-godkjenning
        </div>
        <h2 className="text-lg sm:text-xl font-semibold mt-1">Sikre og arkiver timer per måned</h2>
        <p className="text-xs sm:text-sm text-white/80 mt-2 max-w-2xl">
          Når en måned godkjennes, låses alle timer i den måneden – ingen kan endre dem etterpå.
          Måneden får et permanent PDF-snapshot som kan sendes til regnskap.
        </p>
      </Card>

      <div className="space-y-2" data-testid="time-months-list">
        {months.length === 0 && (
          <Card className="p-6 text-sm text-[#2B3F4D]">
            Ingen timer registrert ennå.
          </Card>
        )}
        {months.map((m) => (
          <Card key={m.month} data-testid={`time-month-${m.month}`}
                className={`p-3 sm:p-4 ${m.locked ? "border-[#1DA3B3]/40 bg-[#1DA3B3]/[0.03]" : ""}`}>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-base sm:text-lg font-bold text-[#0C1824] capitalize">
                    {fmtMonth(m.month)}
                  </span>
                  {m.locked ? (
                    <Badge className="bg-[#1DA3B3]/15 text-[#1DA3B3] border-0">
                      <Lock className="w-3 h-3 mr-1" /> Godkjent og låst
                    </Badge>
                  ) : (
                    <Badge className="bg-[#BD984D]/15 text-[#A57F39] border-0">
                      Åpen
                    </Badge>
                  )}
                </div>
                <div className="text-[11px] sm:text-xs text-[#2B3F4D] mt-1 break-words">
                  <strong>{m.total_hours} t</strong> · {m.entry_count} oppføring{m.entry_count === 1 ? "" : "er"}
                  {m.approved_at && (
                    <> · godkjent {new Date(m.approved_at).toLocaleDateString("nb-NO")} av {m.approved_by_name}</>
                  )}
                </div>
                {m.note && <div className="text-[11px] sm:text-xs text-[#0C1824] mt-1 italic break-words">«{m.note}»</div>}
              </div>
              <div className="flex gap-2 flex-wrap sm:flex-nowrap sm:shrink-0">
                <Button variant="outline" onClick={() => downloadPdf(m.month)}
                        disabled={busy[m.month] === "pdf"}
                        data-testid={`time-month-pdf-${m.month}`}
                        className="border-[#0C1824] text-[#0C1824] hover:bg-[#0C1824]/5 flex-1 sm:flex-none">
                  <FileText className="w-4 h-4 mr-1.5" />
                  {busy[m.month] === "pdf" ? "Laster..." : "Last ned PDF"}
                </Button>
                {!m.locked ? (
                  <Button onClick={() => { setApproveOpen(m.month); setApproveNote(""); }}
                          data-testid={`time-month-approve-${m.month}`}
                          className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white flex-1 sm:flex-none">
                    <ShieldCheck className="w-4 h-4 mr-1.5" /> Godkjenn
                  </Button>
                ) : user?.is_super_admin ? (
                  <Button variant="outline" onClick={() => unlock(m.month)}
                          disabled={busy[m.month] === "unlock"}
                          data-testid={`time-month-unlock-${m.month}`}
                          className="border-red-200 text-red-600 hover:bg-red-50 flex-1 sm:flex-none">
                    <Unlock className="w-4 h-4 mr-1.5" /> Lås opp
                  </Button>
                ) : null}
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!approveOpen} onOpenChange={(o) => !o && setApproveOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-[#BD984D]" />
              Godkjenn {approveOpen && fmtMonth(approveOpen)}
            </DialogTitle>
            <DialogDescription>
              Når du godkjenner låses <strong>alle</strong> timer i måneden. Ingen vil kunne endre,
              legge til eller fjerne timer i {approveOpen && fmtMonth(approveOpen)}. PDF-snapshot
              kan lastes ned umiddelbart etter godkjenning.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-xs">Notat (valgfri – f.eks. «Sendt til regnskap»)</Label>
            <Textarea rows={3} value={approveNote}
                      onChange={(e) => setApproveNote(e.target.value)}
                      data-testid="time-month-approve-note" />
          </div>
          <DialogFooter>
            <Button onClick={() => approve(approveOpen)}
                    disabled={!approveOpen || busy[approveOpen] === "approve"}
                    data-testid="time-month-approve-confirm"
                    className="bg-[#1DA3B3] hover:bg-[#2FB5C5] text-white">
              <Lock className="w-4 h-4 mr-1.5" /> Bekreft godkjenning
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function TimesheetPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [active, setActive] = useState(null);
  const [entries, setEntries] = useState([]);
  const [note, setNote] = useState("");
  const [editing, setEditing] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const polledRef = useRef(false);

  const refresh = () => {
    const params = {};
    if (range.from) params.date_from = range.from;
    if (range.to) params.date_to = range.to;
    Promise.all([
      api.get("/time/active").then((r) => r.data).catch(() => null),
      api.get("/time", { params }).then((r) => r.data).catch(() => []),
    ]).then(([a, list]) => {
      setActive(a);
      setEntries(list);
    });
  };

  useEffect(() => {
    refresh();
    if (polledRef.current) return;
    polledRef.current = true;
  }, [range.from, range.to]);

  const start = async () => {
    try {
      await api.post("/time/start", { note: note.trim() });
      setNote("");
      toast.success("Timer startet");
      refresh();
    } catch { toast.error("Kunne ikke starte"); }
  };
  const stop = async () => {
    try {
      await api.post("/time/stop");
      toast.success("Timer stoppet – husk å bekrefte");
      refresh();
    } catch { toast.error("Kunne ikke stoppe"); }
  };
  const confirm = async (id) => {
    await api.post(`/time/${id}/confirm`);
    toast.success("Bekreftet");
    refresh();
  };
  const saveEdit = async (id, patch) => {
    try {
      await api.patch(`/time/${id}`, patch);
      setEditing(null);
      toast.success("Oppdatert – bekreft på nytt");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke lagre");
    }
  };
  const removeEntry = async (id) => {
    if (!window.confirm("Slette denne timeoppføringen? Dette kan ikke angres.")) return;
    try {
      await api.delete(`/time/${id}`);
      toast.success("Slettet");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke slette");
    }
  };

  return (
    <div className="space-y-5 sm:space-y-6">
      <div>
        <div className="text-[10px] sm:text-xs uppercase tracking-[0.32em] text-[#A57F39]">Tid</div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0C1824] mt-1">Timeliste</h1>
        <p className="text-xs sm:text-sm text-[#2B3F4D] mt-1 max-w-2xl">
          Start når du begynner, stopp når du er ferdig, og bekreft at timene stemmer. Timer
          kan justeres frem til måneden godkjennes – etter det er listen sikret og arkivert.
        </p>
      </div>

      {isAdmin ? (
        <Tabs defaultValue="mine">
          <div className="overflow-x-auto -mx-1 pb-1 scrollbar-thin">
            <TabsList className="w-max min-w-full">
              <TabsTrigger value="mine" data-testid="time-tab-mine">Mine timer</TabsTrigger>
              <TabsTrigger value="staff" data-testid="time-tab-staff">Ansatte</TabsTrigger>
              <TabsTrigger value="activity" data-testid="time-tab-activity">Aktivitet</TabsTrigger>
              <TabsTrigger value="report" data-testid="time-tab-report">Rapport</TabsTrigger>
              <TabsTrigger value="months" data-testid="time-tab-months">Måneder</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="mine" className="mt-5">
            <MyTimesheet active={active} entries={entries} note={note} setNote={setNote}
                         onStart={start} onStop={stop} onConfirm={confirm}
                         onSaveEdit={saveEdit} onDelete={removeEntry}
                         editing={editing} setEditing={setEditing}
                         range={range} setRange={setRange} />
          </TabsContent>
          <TabsContent value="staff" className="mt-5">
            <AdminManageStaff refreshOwn={refresh} />
          </TabsContent>
          <TabsContent value="activity" className="mt-5">
            <AdminActivity />
          </TabsContent>
          <TabsContent value="report" className="mt-5">
            <AdminReport />
          </TabsContent>
          <TabsContent value="months" className="mt-5">
            <AdminMonths user={user} onChanged={refresh} />
          </TabsContent>
        </Tabs>
      ) : (
        <MyTimesheet active={active} entries={entries} note={note} setNote={setNote}
                     onStart={start} onStop={stop} onConfirm={confirm}
                     onSaveEdit={saveEdit} onDelete={removeEntry}
                     editing={editing} setEditing={setEditing}
                     range={range} setRange={setRange} />
      )}
    </div>
  );
}
